import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  AlertCircle,
  Building2,
  CalendarPlus,
  Clock,
  Globe,
  KeyRound,
  ShieldCheck,
  UserPen,
  UserRound,
  Zap,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { AvatarPicker } from "@/components/avatar-picker";
import { NoticeDialog } from "@/components/notice-dialog";
import { type Employee, EmployeeForm } from "@/components/employee-form";
import { Modal } from "@/components/ui/dialog";
import { useCompactLabels } from "@/components/ui/fk-select";
import {
  type DetailContext,
  DetailProperty,
  Mono,
  type ResourceConfig,
  ResourcePage,
  type RowAction,
} from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/services/api";
import type { Translate, TranslationKey } from "@/constants/i18n";
import { profileQuery } from "@/services/queries";
import {
  type EmployeeAccess,
  showsPasswordRequest,
  useEmployeeAccess,
} from "@/utils/employee-access";
import { useTranslation } from "@/store/locale";

/**
 * The three an employee is actually filtered by.
 *
 * `inactive` and `deleted` are states an account is put into rather than
 * states it is in — the archive icon is how the deleted ones are read, and
 * offering them here as well would be two controls for one list. `STATUS`
 * below still names all five, because a badge has to be able to draw
 * whatever the row came back as.
 */
const FILTERABLE_STATUSES = ["active", "account_locked", "password_expired"] as const;

const STATUS: Record<
  Employee["status"],
  { label: TranslationKey; tone: "success" | "danger" | "neutral" }
> = {
  active: { label: "emp.statusActive", tone: "success" },
  account_locked: { label: "emp.statusLocked", tone: "danger" },
  password_expired: { label: "emp.statusExpired", tone: "danger" },
  inactive: { label: "emp.statusInactive", tone: "neutral" },
  deleted: { label: "emp.statusDeleted", tone: "neutral" },
};

const role = (t: Translate, row: Employee) =>
  row.is_superuser ? t("shell.roleSuperuser") : row.groups.map((g) => g.name).join(", ") || "—";

/**
 * Where this person works today.
 *
 * A posting that is running right now *is* the current club — the column says
 * where to find them, not where they are filed — so it takes the top line and
 * is marked Temporary, with the home club named underneath. A posting that is
 * saved but not yet started, or already ended, leaves the home club in place
 * and reads as the footnote it is.
 */
function ClubCell({ row }: { row: Employee }) {
  const t = useTranslation();
  const active = row.is_temporary_club && row.temporary_club && row.has_active_temporary_club;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="text-fg">
        {active ? row.temporary_club!.club_name : (row.club?.club_name ?? "—")}
      </span>
      {active ? <Badge tone="accent">{t("emp.temporaryBadge")}</Badge> : null}
    </span>
  );
}

/** Exported so the toolbar and column contract can be asserted without a list. */
export const employeesConfig = (
  t: Translate,
  openRequest: (row: Employee) => void,
  access: EmployeeAccess,
): ResourceConfig<Employee> => ({
  title: t("nav.employees"),
  singular: t("log.employee"),
  endpoint: "/users",
  model: "user",
  // Newest first, off the primary key — the same default the members list and
  // the geography screens take.
  defaultSort: { key: "id", order: "desc" },
  // A small box and a funnel on the left, the screen's four actions on the
  // right — no worded Filters, no Columns, no Export. The search matches the
  // employee number and the name.
  searchPlaceholder: t("rp.searchShort"),
  // Three characters before the box asks, as on Members. Below that a trigram
  // index has no whole trigram to look up, so the query degrades to a scan for
  // an answer of thousands of rows nobody wanted.
  searchMinLength: 3,
  // Employee Number, Name, Club, Role, Status — and the name is the way into
  // the record, since the id is not one of them.
  viewColumn: "full_name",
  confirmDeleteMessage: t("emp.confirmDelete"),
  columns: [
    {
      key: "user_name",
      label: t("emp.employeeNumber"),
      sortKey: "user_name",
      render: (row) => <Mono>{row.user_name}</Mono>,
    },
    // The email sits under the name and the posting under the club, as they
    // always have. What is new is that each has its own switch: turned off in
    // Customize Columns, the second line simply is not drawn.
    {
      key: "full_name",
      label: t("emp.name"),
      sortKey: "name",
      fields: ["full_name", "user_name", "is_password_request", "groups", "is_superuser"],
      render: (row) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-fg">{row.full_name || "—"}</span>
          {/* The same rule the kebab entry follows, so the badge and the
              action it points at can never be out of step. */}
          {showsPasswordRequest(row, access) ? (
            <Badge tone="warning">{t("emp.passwordRequest")}</Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: "club",
      label: t("emp.club"),
      fields: ["club", "temporary_club", "temporary_start_date", "temporary_end_date"],
      render: (row) => <ClubCell row={row} />,
    },
    { key: "role", label: t("emp.role"), render: (row) => role(t, row) },
    {
      key: "status",
      label: t("emp.status"),
      render: (row) => (
        <Badge tone={STATUS[row.status].tone}>{t(STATUS[row.status].label)}</Badge>
      ),
    },
  ],
  filters: [
    { name: "club_id", label: t("emp.club"), type: "fk", endpoint: "/clubs/compact" },
    { name: "group_id", label: t("emp.role"), type: "fk", endpoint: "/groups/compact" },
    { name: "created_from", label: t("geo.createdFrom"), type: "date" },
    { name: "created_to", label: t("geo.createdTo"), type: "date" },
    {
      name: "status",
      label: t("emp.status"),
      type: "select",
      options: FILTERABLE_STATUSES.map((value) => ({ value, label: t(STATUS[value].label) })),
    },
    {
      name: "temporary_club_id",
      label: t("emp.temporaryClubFilter"),
      type: "fk",
      endpoint: "/clubs/compact",
    },
  ],
  fields: [],
  toForm: () => ({}),
  toolbarVariant: "compact",
  // Four actions wide already; Refresh and Archive read as icons beside the
  // two worded ones rather than making it six.
  iconRefresh: true,
  // Five columns, all of them shown: there is nothing behind a picker, so
  // the screen offers none. (Compact keeps the picker for lists that do.)
  columnPicker: false,
  // Archiving an employee is deleting them, so the archive is the deleted
  // ones — and reading that list is the same right as putting somebody on it.
  archiveFilter: { name: "status", value: "deleted", permission: "delete__user" },
  // Tick the rows, then one button above them: Delete on the list, Restore in
  // the archive. Behind the same right as deleting one — whoever may put an
  // account in the archive may take it out. The server tries each on its own
  // and names the ones it would not move, so a batch of forty that includes
  // your own account and a superuser's comes back with those two and the
  // reason, rather than failing whole.
  bulk: { permission: "delete__user" },
  restore: { permission: "delete__user" },
  // The log covers every club, so it is a manager's screen and not a
  // supervisor's. Hidden rather than disabled: it never becomes available.
  toolbar: access.canOpenLog ? (
    // Outlined in the accent rather than the neutral border the other
    // secondary buttons take: it is the one control here that leaves the
    // screen, and the row's only worded action besides Add.
    <Button asChild variant="secondary" className="border-accent text-accent">
      <Link to="/employee-activity-log">{t("page.activityLog")}</Link>
    </Button>
  ) : undefined,
  // A sheet over the list, not a page in place of it: the form is short, and
  // the table behind is the context for what is being added to it.
  formVariant: "modal",
  // Read over the list, not in place of it: the row it came from stays in view.
  viewVariant: "modal",
  renderView: (row, ctx) => <EmployeeView row={row} ctx={ctx} />,
  renderForm: (editing, onClose, onSaved) => (
    <EmployeeForm
      employee={editing === "new" ? null : editing}
      onClose={onClose}
      onSaved={onSaved}
    />
  ),
  // A superuser short-circuits every permission check, so deleting one locks
  // everybody out of what only it can reach — and editing someone else's is an
  // escalation path: change the address, then ask for a reset to it. The API
  // refuses both; these stop offering the buttons.
  rowCanDelete: (row) => !row.is_superuser,
  rowCanEdit: (row, profile) => !row.is_superuser || profile?.id === row.id,
  // Read in the order someone asks about an account: who they are, whether it
  // works, then the password trail. Created and updated stay in the footnote
  // after all of it — a record's own history reads last.
  detailSections: [
    // The picture leads, the way it does on a profile — it was under every
    // field in a slab of its own at the bottom.
    { title: t("emp.sectionPicture"), render: (row) => <EmployeePicture row={row} /> },
    { title: t("emp.sectionIdentity"), fields: ["user_name", "email_address"] },
    {
      title: t("emp.sectionAccess"),
      fields: ["status", "is_superuser", "manager_id"],
      // The role is a list of group objects, which the generic renderer skips
      // — and the one thing Access is read for. Said here in words.
      render: (row) => (
        <DetailProperty label={t("emp.role")} icon={ShieldCheck}>
          {role(t, row)}
        </DetailProperty>
      ),
    },
    // Its own section, straight after Access, rather than an afterthought
    // below the whole record: where someone works is not a footnote.
    { title: t("emp.sectionClub"), render: (row) => <EmployeeClub row={row} /> },
    {
      title: t("emp.sectionPassword"),
      fields: [
        "force_password_change",
        "is_password_request",
        "request_reason",
        "password_request_at",
        "last_password_change_at",
        "last_login_at",
      ],
    },
  ],
  detailLabels: {
    user_name: t("emp.employeeNumber"),
    email_address: t("emp.emailAddress"),
    status: t("emp.status"),
    is_superuser: t("emp.isSuperuser"),
    manager_id: t("emp.manager"),
    force_password_change: t("emp.forcePasswordChange"),
    is_password_request: t("emp.isPasswordRequest"),
    request_reason: t("emp.requestReason"),
    password_request_at: t("emp.requestedAt"),
    last_password_change_at: t("emp.lastPasswordChange"),
    last_login_at: t("emp.lastLogin"),
  },
  detailHide: [
    "avatar",
    // Null on most accounts, so it survives the "no objects" rule and shows as
    // an empty row; the Club section below says it properly when there is one.
    "temporary_club",
    "club",
    // The Club section below says all of this, with the club's name rather
    // than its id and the window the posting applies over.
    "club_id",
    "temporary_club_id",
    "effective_club_id",
    // `is_active` is `status` read a second way.
    "is_active",
    "is_temporary_club",
    "has_active_temporary_club",
    "temporary_club_reason",
    "temporary_start_date",
    "temporary_end_date",
  ],
  rowActions: (row) => rowActions(t, row, openRequest, access),
});

function EmployeesPage() {
  const t = useTranslation();
  const [request, setRequest] = useState<Employee | null>(null);
  const queryClient = useQueryClient();
  const access = useEmployeeAccess();
  const cfg = useMemo(() => employeesConfig(t, setRequest, access), [t, access]);
  return (
    <>
      <ResourcePage config={cfg} />
      {request ? (
        <PasswordRequestDialog
          row={request}
          onClose={() => setRequest(null)}
          onDone={() => void queryClient.invalidateQueries({ queryKey: ["/users"] })}
        />
      ) : null}
    </>
  );
}

/**
 * What the kebab holds beyond View, Edit and Delete: one entry, conditionally.
 *
 * Reject is deliberately not here — refusing somebody their password is a
 * decision to take with the reason in front of you, so it is a button inside
 * the modal and not a second line on a menu opened by mistake.
 */
function rowActions(
  t: Translate,
  row: Employee,
  openRequest: (row: Employee) => void,
  access: EmployeeAccess,
): RowAction[] {
  if (!showsPasswordRequest(row, access)) return [];
  return [
    {
      label: t("emp.passwordRequestAction"),
      icon: Zap,
      afterDelete: true,
      run: () => openRequest(row),
    },
  ];
}

/**
 * The request as the person made it, and the two answers to it.
 *
 * The shared notice shape, so this looks like every other decision the app
 * stops to ask for. Reject is deliberately not a row-menu entry: refusing
 * somebody their password is a decision to take with the reason in front of
 * you.
 */
export function PasswordRequestDialog({
  row,
  onClose,
  onDone,
}: {
  row: Employee;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  /**
   * Why the answer did not go through — most often an associate with no
   * manager on file, or a manager with no address. It belongs here rather
   * than in a toast: the modal stays open, the request stays pending, and
   * the person who pressed the button is looking at this.
   */
  const [error, setError] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: (verb: "approve" | "reject") =>
      api.post(`/users/${row.id}/password-request/${verb}`),
    onSuccess: (_, verb) => {
      toast.success(verb === "approve" ? t("emp.requestSent") : t("emp.requestRejected"));
      onDone();
      ref.current?.close();
    },
    // The request is left standing on a failure, so the decision can be
    // retaken rather than silently lost.
    onError: (err) => setError(errorMessage(err, t("emp.requestFailed"))),
  });
  const busy = act.isPending;
  const answer = (verb: "approve" | "reject") => {
    setError(null);
    act.mutate(verb);
  };
  return (
    <NoticeDialog
      dialogRef={ref}
      icon={KeyRound}
      tone="warning"
      title={t("emp.passwordRequestTitle")}
      description={t("emp.requestedReset").replace("{name}", row.full_name || row.user_name)}
      /* On its own line and in the danger colour: it is what the decision
         turns on, and it should not read as one detail among many. */
      extra={
        <>
          <p className="text-danger text-callout mt-1 font-medium">
            {t("emp.reasonLabel").replace("{reason}", requestReason(t, row))}
          </p>
          {error ? (
            <p
              role="alert"
              className="bg-danger-soft text-danger text-subhead mt-3 flex items-start gap-2 rounded-lg px-3 py-2"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>{error}</span>
            </p>
          ) : null}
        </>
      }
      onClose={onClose}
      actions={
        <>
          <Button
            variant="secondary"
            disabled={busy}
            loading={busy && act.variables === "reject"}
            loadingLabel={t("emp.reject")}
            onClick={() => answer("reject")}
          >
            {t("emp.reject")}
          </Button>
          <Button
            disabled={busy}
            loading={busy && act.variables === "approve"}
            loadingLabel={t("emp.approve")}
            onClick={() => answer("approve")}
          >
            {t("emp.approve")}
          </Button>
        </>
      }
    />
  );
}

/** The three the sign-in screen offers, said the way this screen says them. */
const REQUEST_REASONS: Record<string, TranslationKey> = {
  forgot_password: "emp.reasonForgot",
  account_locked: "emp.reasonLocked",
  password_expired: "emp.reasonExpired",
};

const requestReason = (t: Translate, row: Employee) => {
  const key = row.request_reason ? REQUEST_REASONS[row.request_reason] : undefined;
  // An unrecognised code is shown as it came rather than swallowed: the
  // decision is the reader's, and a blank line tells them nothing.
  return key ? t(key) : (row.request_reason ?? "—");
};

/**
 * The record, read-only, over the list it was opened from.
 *
 * Deliberately narrow: who they are, where they work, and whether the account
 * works — the questions the list raises. There is no form here, so nothing to
 * save and no password to generate; the employee number is shown and cannot
 * be anything else. A posting appears only when one is actually saved.
 */
export function EmployeeView({ row, ctx }: { row: Employee; ctx: DetailContext }) {
  const t = useTranslation();
  // Names for the two authorship ids, from whatever the session has already
  // seen. An id nothing can name is shown as an id rather than as a blank.
  const people = useCompactLabels("/users/compact");
  const who = (id: number | null) =>
    id === null ? "—" : (people[String(id)] ?? String(ctx.lookup("manager_id", id)));
  const when = (value: string | null | undefined) =>
    value ? format(new Date(value), "MM/dd/yyyy hh:mm a") : "—";

  return (
    <Modal open onClose={ctx.onClose} size="lg" title={t("emp.viewTitle")}>
      {/* Five rows, one column, every value starting at the same place — the
          label column is widened past the shared default so the two read as
          two columns rather than as ragged pairs. Nothing is conditional, so
          the modal is the same height for every employee. */}
      <dl className="grid [&>div]:grid-cols-[1rem_minmax(0,14rem)_minmax(0,1fr)]">
        <DetailProperty label={t("emp.employeeNumber")}>{row.user_name}</DetailProperty>
        <DetailProperty label={t("emp.name")}>{row.full_name || "—"}</DetailProperty>
        <DetailProperty label={t("emp.club")} icon={Building2}>
          {/* Where they work today — the posting when one is running, the home
              club otherwise — and no second line about the window. */}
          <ClubCell row={row} />
        </DetailProperty>
        <DetailProperty label={t("emp.role")} icon={ShieldCheck}>
          {role(t, row)}
        </DetailProperty>
        <DetailProperty label={t("emp.status")} icon={Globe}>
          <Badge tone={STATUS[row.status].tone}>{t(STATUS[row.status].label)}</Badge>
        </DetailProperty>
      </dl>

      {/* Who touched the record and when — the footnote, after all of it. */}
      <dl className="hairline-t text-subhead text-fg-subtle mt-5 grid gap-x-8 gap-y-3 pt-4 sm:grid-cols-2 lg:grid-cols-4">
        {(
          [
            { label: "rp.stampCreatedBy", icon: UserRound, value: who(row.created_by_id) },
            { label: "rp.stampCreatedAt", icon: CalendarPlus, value: when(row.created_at) },
            { label: "rp.stampUpdatedBy", icon: UserPen, value: who(row.updated_by_id) },
            { label: "rp.stampUpdatedAt", icon: Clock, value: when(row.updated_at) },
          ] as const
        ).map((stamp) => (
          <div key={stamp.label} className="min-w-0">
            <dt className="flex items-center gap-1.5">
              <stamp.icon className="size-3.5 shrink-0" aria-hidden />
              {t(stamp.label)}
            </dt>
            <dd className="text-fg mt-0.5 min-w-0 break-words">{stamp.value}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  );
}

/** The profile picture, and the two ways to change one. */
function EmployeePicture({ row }: { row: Employee }) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const { data: profile } = useQuery(profileQuery());
  // The panel holds the row it opened with, so the new picture is kept here to
  // show at once; the list is invalidated for everywhere else.
  const [picture, setPicture] = useState<string | null | undefined>(undefined);
  const avatar = picture === undefined ? row.avatar : picture;
  const save = useMutation({
    // Its own endpoint: an ordinary edit should not carry a couple of hundred
    // kilobytes of picture on a path that has nothing to do with one.
    mutationFn: (value: string) => api.put(`/users/${row.id}/avatar`, { avatar: value }),
    onSuccess: (_, value) => {
      setPicture(value || null);
      toast.success(value ? t("emp.pictureUpdated") : t("emp.pictureRemoved"));
      void queryClient.invalidateQueries({ queryKey: ["/users"] });
      if (profile?.id === row.id) void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error) => toast.error(errorMessage(error, t("emp.pictureFailed"))),
  });

  return (
    <AvatarPicker
      src={avatar}
      name={row.full_name || row.user_name}
      busy={save.isPending}
      // Same rule as the record itself: a superuser's account is theirs alone.
      editable={!row.is_superuser || profile?.id === row.id}
      onChange={(dataUrl) => save.mutate(dataUrl)}
      onRemove={() => save.mutate("")}
    />
  );
}

/** Where they work: the role they hold, the club, and any posting over it. */
function EmployeeClub({ row }: { row: Employee }) {
  const t = useTranslation();
  return (
    <dl className="grid gap-x-12 md:grid-cols-2">
      <DetailProperty label={t("emp.role")} icon={ShieldCheck}>
        {role(t, row)}
      </DetailProperty>
      <DetailProperty label={t("emp.club")} icon={Building2}>
        <ClubCell row={row} />
      </DetailProperty>
      {row.is_password_request ? (
        <DetailProperty label={t("emp.passwordRequest")}>
          {row.request_reason ?? "—"}
        </DetailProperty>
      ) : null}
    </dl>
  );
}

export default EmployeesPage;
